import { useEffect, useMemo, useState } from 'react'

import { useQuery, useQueryClient } from '@tanstack/react-query'
import { Download, RotateCcw, Sparkle } from 'lucide-react'
import { useFormContext, useWatch } from 'react-hook-form'

import { getTMinus, toISO8601 } from '@fortem-monorepo/js-utilities'
import { HeaderConfigBar, useApi } from '@fortem-monorepo/react-spa'
import { ViewContainer } from '@fortem-monorepo/react-spa'
import { useFormWithSchema } from '@fortem-monorepo/react-spa'
import { DateRangePicker } from '@fortem-monorepo/react-ui'
import { FormError } from '@fortem-monorepo/react-ui'
import { LoadingButton } from '@fortem-monorepo/react-ui'
import { SelectInput } from '@fortem-monorepo/react-ui'
import { SubmitButton } from '@fortem-monorepo/react-ui'
import { DataTableFacetedFilter } from '@fortem-monorepo/react-ui'
import { Form, FormField, FormItem, FormLabel } from '@fortem-monorepo/react-ui'
import { Separator } from '@fortem-monorepo/react-ui'

import { Page } from '@/components/Page'
import { INSTRUMENTS } from '@/constants/links'
import { Database } from '@/pages/instruments/time-series/constants'
import {
  TimeSeriesSchemaType,
  timeSeriesSchema,
} from '@/pages/instruments/time-series/time-series.schema'
import { useDownloadTimeSeriesMutation } from '@/react-query/mutations/misc'
import { useInstrumentsByTypeQuery } from '@/react-query/queries/instruments'

export interface TimeSeriesPageProps {}

export const TimeSeriesPage: React.FC<TimeSeriesPageProps> = ({}) => {
  const downloadTimeSeriesMutation = useDownloadTimeSeriesMutation()

  const form = useFormWithSchema(
    {
      mode: 'all',
      defaultValues: {
        // database: "new",
        dates: {
          from: getTMinus(21),
          to: getTMinus(1),
        },
        instrument_type_ids: [],
        instrument_ids: [],
      },
    },
    timeSeriesSchema,
  )

  const { handleSubmit, watch } = form

  const onSubmit = handleSubmit(({ dates: { from, to }, database: db, ...data }) => {
    const st_dt = toISO8601(from)
    const ed_dt = toISO8601(to ?? st_dt)

    downloadTimeSeriesMutation.mutateAsync({
      st_dt,
      ed_dt,
      db,
      ...data,
    })
  })

  const client = useQueryClient()
  const instrument_type_ids = watch('instrument_type_ids')

  const handleRefreshDropdowns = async () => {
    const queryKeys = [
      ...instrument_type_ids.map((id) => ['instruments', 'type', id]),
      ['instruments', 'types'],
    ]
    await Promise.all(queryKeys.map((queryKey) => client.invalidateQueries({ queryKey })))
  }

  const handleClearInstruments = async () => {
    form.setValue('instrument_type_ids', [], { shouldValidate: true })
    form.setValue('instrument_ids', [], { shouldValidate: true })
  }

  return (
    <Page metadata={{ title: 'Time Series' }} permissions={INSTRUMENTS.TIME_SERIES.permissions}>
      <HeaderConfigBar>
        <LoadingButton size="sm" variant={'link'} onClick={handleRefreshDropdowns}>
          <Sparkle /> Refresh dropdown data
        </LoadingButton>
        <LoadingButton size="sm" variant={'link'} onClick={handleClearInstruments}>
          <RotateCcw /> Clear Instruments
        </LoadingButton>
      </HeaderConfigBar>
      <Form {...form}>
        <form onSubmit={onSubmit}>
          <PageContent />
        </form>
      </Form>
    </Page>
  )
}

interface PageContentProps {}

const PageContent: React.FC<PageContentProps> = ({}) => {
  const {
    control,
    setValue,
    formState: { isValid, isSubmitting },
  } = useFormContext<TimeSeriesSchemaType>()

  const api = useApi()

  const [mounted, setMounted] = useState(false)

  useEffect(() => {
    if (!mounted) {
      setTimeout(() => setMounted(true), 100)
    }
  }, [])

  const query = useQuery({
    queryKey: ['instruments', 'types'],
    queryFn: () => api.instruments.getInstrumentTypes(),
    enabled: mounted,
  })

  const options = useMemo(() => {
    return (query.data ?? []).map((o) => ({
      label: o.label,
      value: o.value?.toString(),
    }))
  }, [query.data])

  const [instrument_type_ids] = useWatch({ control, name: ['instrument_type_ids'] })

  return (
    <ViewContainer className={'flex flex-col gap-6'}>
      <h1 className={'text-3xl font-medium text-gray-600'}>Download Time Series</h1>
      <p className={'text-gray-700 text-sm'}>
        You may only download data for a maximum of <strong>20</strong> instruments at a given time.
      </p>
      {/* <Separator /> */}
      <div className={'flex flex-col gap-6'}>
        <div className="flex items-center gap-2 bg-sidebar p-4 rounded-md border">
          <FormField
            name="instrument_type_ids"
            control={control}
            render={({ field: { value } }) => {
              return (
                <FormItem>
                  <div className="flex items-center gap-2">
                    <FormLabel>Instrument Types</FormLabel>
                    <FormError />
                  </div>
                  <DataTableFacetedFilter
                    disabled={query.isFetching}
                    className={'min-w-80'}
                    showToggleAll={false}
                    title={'Select Instrument Types'}
                    options={options}
                    values={value?.map((v) => v?.toString())}
                    setValues={(val) => {
                      setValue(
                        'instrument_type_ids',
                        val.map((v) => Number(v)),
                        { shouldValidate: true },
                      )
                    }}
                  />
                </FormItem>
              )
            }}
          />
          <FormField
            name="dates"
            control={control}
            render={({ field: { value } }) => {
              return (
                <FormItem>
                  <div className="flex items-center gap-2">
                    <FormLabel>Dates</FormLabel>
                    <FormError />
                  </div>
                  <DateRangePicker
                    date={value}
                    setDate={(dates) => {
                      if (dates) {
                        let _from = dates.from
                        let _to = dates.to ?? _from

                        if (_from && _to) {
                          dates &&
                            setValue('dates', { from: _from, to: _to }, { shouldValidate: true })
                        }
                      }
                    }}
                  />
                </FormItem>
              )
            }}
          />
          <FormField
            name="database"
            control={control}
            render={({ field: { value } }) => {
              return (
                <FormItem>
                  <div className="flex items-center gap-2">
                    <FormLabel>Databases</FormLabel>
                    <FormError />
                  </div>
                  <SelectInput
                    placeholder={'Pick DB'}
                    value={value}
                    onValueChange={(val) => {
                      setValue('database', val as Database, { shouldValidate: true })
                    }}
                    options={['new', 'old', 'both'].map((o) => ({
                      label: o.toUpperCase(),
                      value: o,
                    }))}
                  />
                </FormItem>
              )
            }}
          />
        </div>

        {instrument_type_ids.length > 0 && (
          <>
            <Separator />
            <div className={'grid xl:grid-cols-2 gap-4 items-center w-full max-w-350'}>
              {instrument_type_ids.map((id) => {
                return (
                  <div
                    className={'p-4 border rounded-md bg-sidebar flex flex-1'}
                    key={id?.toString()}
                  >
                    <InstrumentSelector
                      label={options.find((o) => Number(o.value) === id)?.['label']}
                      instrument_type_id={id}
                    />
                  </div>
                )
              })}
            </div>
          </>
        )}

        <FormItem>
          <div>
            <SubmitButton loading={isSubmitting} icon={<Download />} disabled={!isValid}>
              Download selected
            </SubmitButton>
          </div>
        </FormItem>
      </div>
    </ViewContainer>
  )
}

export interface InstrumentSelectorProps {
  instrument_type_id: number
  label: string | undefined
}

export const InstrumentSelector: React.FC<InstrumentSelectorProps> = ({
  instrument_type_id,
  label,
}) => {
  const { control, setValue } = useFormContext<TimeSeriesSchemaType>()
  const query = useInstrumentsByTypeQuery([instrument_type_id])

  const options = useMemo(() => {
    return (query.data ?? []).map((o) => ({
      label: o.label,
      value: o.value?.toString(),
    }))
  }, [query.data])

  return (
    <FormField
      name="instrument_ids"
      control={control}
      render={({ field: { value } }) => {
        const optionValues = options.map((o) => Number(o.value))
        const localValues = value?.filter((v) => optionValues.includes(v)).map((v) => v?.toString())

        return (
          <FormItem className={'w-full'}>
            <FormLabel>{label}</FormLabel>
            <DataTableFacetedFilter
              options={options}
              title={`Select ${label?.toLowerCase()}`}
              values={localValues}
              setValues={(val) => {
                const newLocalValues = val.map((v) => Number(v))
                const allOtherValues = value.filter((v) => !optionValues.includes(v))

                setValue('instrument_ids', [...allOtherValues, ...newLocalValues], {
                  shouldValidate: true,
                })
              }}
            />
          </FormItem>
        )
      }}
    />
  )
}
